import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import LoadingInline from '../../../components/Loading/LoadingInline'
import { iOption } from '../../../interfaces/iOption'
import { selectFormIsFullInit } from '../../../redux/formTree/formTreeSelectors'
import { selectScopeToLabel, selectScopeToLabelReq } from '../../../redux/submissions/selectors'
import EnumLoader from '../../../services/EnumLoader'
import FormFullInit from '../../../services/FormFullInit'
import { useElementContext } from '../../context/ElementContext'
import { useFormRenderContext } from '../../context/RenderFormContext'
import { useElementEnum } from '../../hooks/useElementEnum'
import { useElementEnumDynamic } from '../../hooks/useElementEnumDynamic'
import PartGovLabel from '../parts/PartGovLabel'
import WrapInputGov from '../parts/WrapInputGov'
import { useControlRegister } from '../useControlRegister'

const groupOptionsByLabel = (options: iOption[], scopeToLabelsForm: { [scope: string]: string }) => {
  const groupedOptionsScopes: Record<string, iOption[]> = {}
  options.forEach((opt) => {
    const group = opt.group || 'Ostatní'
    if (!groupedOptionsScopes[group]) groupedOptionsScopes[group] = []
    groupedOptionsScopes[group].push(opt)
  })

  const groupOrderKeys = Object.keys(scopeToLabelsForm)
  const sortedGroupsScope = Object.keys(groupedOptionsScopes).sort((a, b) => {
    const indexA = groupOrderKeys.indexOf(a)
    const indexB = groupOrderKeys.indexOf(b)
    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
  })

  const groupedOptions: { [label: string]: iOption[] } = {}
  sortedGroupsScope.map((scope) => {
    const label = scopeToLabelsForm[scope]
    groupedOptions[label] = groupedOptionsScopes[scope]
  })

  return groupedOptions
}

const InputSelect = () => {
  const { field, errors } = useControlRegister()
  const { id, required, disabled, path } = useElementContext()
  const { formSchemaId } = useFormRenderContext()
  const { enumOptions: staticEnumOptions, enumLoading, enumKey } = useElementEnum()
  const { isDynamicEnum, dynamicEnumOptions } = useElementEnumDynamic()

  const { isReq } = useFormRenderContext()
  const scopeToLabelsForm = useSelector(isReq ? selectScopeToLabelReq : selectScopeToLabel)

  const enumOptions = isDynamicEnum ? dynamicEnumOptions : staticEnumOptions
  const isFullInit = useSelector(selectFormIsFullInit)

  const [loadingFirst, setLoadingFirst] = useState(true)
  const [singleOptions, setSingleOptions] = useState<iOption[]>([])

  useEffect(() => {
    // vymazani hodnoty kdyz se zmeni options a hodnota tam neni
    const existEnumKey = enumOptions?.some((opt) => opt.value === field.value)
    if (
      isFullInit &&
      field.value &&
      !disabled &&
      !existEnumKey &&
      ((isDynamicEnum && enumOptions !== undefined) || enumOptions?.length)
    ) {
      toast.info(`Smazána neplatná hodnota: ${field.value} (${path})`)
      field.onChange('') // undefined nevymaze
    }
  }, [enumOptions, isFullInit])

  useEffect(() => {
    if (field.value && enumKey && disabled && !enumOptions?.length && !singleOptions?.length) {
      //nacteni labelu z ulozene value
      EnumLoader.getByCode(formSchemaId!, enumKey!, field.value)
        .then((res) => {
          setLoadingFirst(false)
          setSingleOptions(res)
        })
        .catch((err) => {
          console.error(err)
          toast.error('Chyba načtení hodnoty do číselníku: ' + field.value)
        })
    } else {
      setLoadingFirst(false)
    }
  }, [field.value])

  useEffect(() => {
    if (!enumLoading && !loadingFirst) {
      FormFullInit.removeInputSelectScopes(path)
    }
  }, [enumLoading, loadingFirst])

  if (enumLoading || loadingFirst) {
    return <LoadingInline />
  }

  const options = singleOptions?.length ? singleOptions : enumOptions
  const groupedOptions = options?.some((o) => o.group) ? groupOptionsByLabel(options, scopeToLabelsForm) : undefined

  return (
    <WrapInputGov value={field.value} errors={errors}>
      <div className={'gov-select ' + (disabled ? 'selectDisabled' : '')}>
        <select
          {...field}
          id={id}
          disabled={disabled}
          aria-required={required ? 'true' : 'false'}
          aria-disabled={disabled ? 'true' : 'false'}
          aria-labelledby={id}
          title={field.value}
        >
          <option value=''></option>

          {groupedOptions
            ? Object.entries(groupedOptions).map(([groupName, groupItems]) => (
                <optgroup label={groupName} key={groupName}>
                  {groupItems.map(({ label, value }) => (
                    <option key={value} value={value} title={label}>
                      {label}
                    </option>
                  ))}
                </optgroup>
              ))
            : options?.map(({ label, value }) => (
                <option key={value} value={value} title={label}>
                  {label}
                </option>
              ))}
        </select>
        <PartGovLabel />
      </div>
    </WrapInputGov>
  )
}

export default InputSelect
